import React, { createRef } from "react";

import cx from "classnames";
import PropTypes from "prop-types";

import { calculateSpeedyMeetingDates } from "~/calendar/utils/meetingGuidelinesUtils";

import DayColumnEvent from "./DayColumnEvent";
import DayColumnWrapper from "./DayColumnWrapper";
import Selection, { getBoundsForNode, isEvent } from "./Selection";
import TimeSlotGroup from "./TimeSlotGroup";
import * as DayEventLayout from "./utils/DayEventLayout";
import { notify } from "./utils/helpers";
import { DayLayoutAlgorithmPropType } from "./utils/propTypes";
import { isSelected } from "./utils/selection";
import * as TimeSlotUtils from "./utils/TimeSlots";

class DayColumn extends React.Component {
	state = { selecting: false, timeIndicatorPosition: null };
	intervalTriggered = false;

	constructor(...args) {
		super(...args);

		this.slotMetrics = TimeSlotUtils.getSlotMetrics(this.props);
		this.containerRef = createRef();
	}

	componentDidMount() {
		this.props.selectable && this._selectable();

		if (this.props.isNow) {
			this.setTimeIndicatorPositionUpdateInterval();
		}
	}

	componentWillUnmount() {
		this._teardownSelectable();
		this.clearTimeIndicatorInterval();
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (nextProps.selectable && !this.props.selectable) this._selectable();
		if (!nextProps.selectable && this.props.selectable) this._teardownSelectable();

		this.slotMetrics = this.slotMetrics.update(nextProps);
	}

	componentDidUpdate(prevProps, prevState) {
		const { getNow, isNow, localizer, date, min, max } = this.props;
		const getNowChanged = localizer.neq(prevProps.getNow(), getNow(), "minutes");

		if (prevProps.isNow !== isNow || getNowChanged) {
			this.clearTimeIndicatorInterval();

			if (isNow) {
				const tail =
					!getNowChanged &&
					localizer.eq(prevProps.date, date, "minutes") &&
					prevState.timeIndicatorPosition === this.state.timeIndicatorPosition;

				this.setTimeIndicatorPositionUpdateInterval(tail);
			}
		} else if (
			isNow &&
			(localizer.neq(prevProps.min, min, "minutes") || localizer.neq(prevProps.max, max, "minutes"))
		) {
			this.positionTimeIndicator();
		}
	}

	/**
	 * @param tail {Boolean} - whether `positionTimeIndicator` call should be
	 *   deferred or called upon setting interval (`true` - if deferred);
	 */
	setTimeIndicatorPositionUpdateInterval(tail = false) {
		if (!this.intervalTriggered && !tail) {
			this.positionTimeIndicator();
		}

		this._timeIndicatorTimeout = window.setTimeout(() => {
			this.intervalTriggered = true;
			this.positionTimeIndicator();
			this.setTimeIndicatorPositionUpdateInterval();
		}, 60000);
	}

	clearTimeIndicatorInterval() {
		this.intervalTriggered = false;
		window.clearTimeout(this._timeIndicatorTimeout);
	}

	positionTimeIndicator() {
		const { min, max, getNow } = this.props;
		const current = getNow();

		if (current >= min && current <= max) {
			const top = this.slotMetrics.getCurrentTimePosition(current);
			this.intervalTriggered = true;
			this.setState({ timeIndicatorPosition: top });
		} else {
			this.clearTimeIndicatorInterval();
		}
	}

	render() {
		const {
			date,
			max,
			rtl,
			isNow,
			resource,
			accessors,
			localizer,
			getters: { dayProp, ...getters },
			components: { eventContainerWrapper: EventContainer, ...components },
		} = this.props;

		let { slotMetrics } = this;
		let { selecting, top, height, startDate, endDate } = this.state;

		let selectDates = { start: startDate, end: endDate };

		const { className, style } = dayProp(max);

		const DayColumnWrapperComponent = components.dayColumnWrapper || DayColumnWrapper;

		return (
			<DayColumnWrapperComponent
				ref={this.containerRef}
				date={date}
				style={style}
				className={cx(className, "fbc-day-slot", "fbc-time-column", {
					"fbc-now": isNow,
					"fbc-today": isNow,
					"fbc-slot-selecting": selecting,
				})}
				slotMetrics={slotMetrics}
			>
				{slotMetrics.groups.map((grp, idx) => (
					// eslint-disable-next-line react/no-array-index-key
					<TimeSlotGroup key={idx} group={grp} resource={resource} getters={getters} components={components} />
				))}
				<EventContainer
					localizer={localizer}
					resource={resource}
					accessors={accessors}
					getters={getters}
					components={components}
					slotMetrics={slotMetrics}
				>
					<div className={cx("fbc-events-container", { rtl })}>
						{this.renderEvents({
							events: this.props.backgroundEvents,
							isBackgroundEvent: true,
						})}
						{this.renderEvents({ events: this.props.events })}
					</div>
				</EventContainer>

				{selecting && (
					<div className="fbc-slot-selection" style={{ top, height }}>
						<span>{localizer.format(selectDates, "selectRangeFormat")}</span>
					</div>
				)}
				{isNow && this.intervalTriggered && (
					<svg className="fbc-current-time-indicator" style={{ top: `${this.state.timeIndicatorPosition}%` }}>
						<rect
							id="bar"
							x="9.5"
							y="4.5"
							width="100%"
							height="3"
							rx="1.5"
							style={{ fill: "var(--attentionColor)", stroke: "var(--white)", strokeWidth: "1px" }}
						/>
						<circle id="circle-fill" cx="6" cy="6" r="5" style={{ fill: "var(--attentionColor)" }} />
						<path
							id="circle-stroke"
							d={`
								M 11.879, 4.801
								c -0.556, -2.738 -2.978, -4.801 -5.879, -4.801
								c -3.311, 0 -6, 2.689 -6, 6
								c 0, 3.311 2.689, 6 6, 6
								c 2.901, 0 5.323, -2.063 5.879, -4.801
								l -0.98, -0.199
								c -0.463, 2.281 -2.482, 4 -4.899, 4
								c -2.76, 0 -5, -2.24 -5, -5
								c 0, -2.76 2.24, -5 5, -5
								c 2.417, 0 4.436, 1.719 4.899, 4
								l 0.98, -0.199Z
							`}
							style={{ fill: "var(--white)" }}
						/>
					</svg>
				)}
			</DayColumnWrapperComponent>
		);
	}

	renderEvents = ({ events, isBackgroundEvent }) => {
		let { selected, accessors, getters, step, timeslots, dayLayoutAlgorithm } = this.props;

		const { slotMetrics } = this;

		let styledEvents = DayEventLayout.getStyledEvents({
			events,
			accessors,
			slotMetrics,
			minimumStartDifference: Math.ceil((step * timeslots) / 2),
			dayLayoutAlgorithm,
		});

		return styledEvents.map(({ event, style }) => {
			let end = accessors.end(event);
			let start = accessors.start(event);

			const startsBeforeDay = slotMetrics.startsBeforeDay(start);
			const startsAfterDay = slotMetrics.startsAfterDay(end);

			let continuesPrior = startsBeforeDay || slotMetrics.startsBefore(start);
			let continuesAfter = startsAfterDay || slotMetrics.startsAfter(end);

			return (
				<DayColumnEvent
					style={style}
					event={event}
					key={`evt_${event.guid}`}
					getters={getters}
					continuesPrior={continuesPrior}
					continuesAfter={continuesAfter}
					selected={isSelected(event, selected)}
					onClick={this.select(event)}
					onDoubleClick={this.doubleClick(event)}
					isBackgroundEvent={isBackgroundEvent}
				/>
			);
		});
	};

	_selectable = () => {
		let node = this.containerRef.current;
		const { longPressThreshold, localizer } = this.props;
		let selector = (this._selector = new Selection(() => node, {
			longPressThreshold: longPressThreshold,
		}));
		const { breaksBetweenMeetingsEnabled, breaksBetweenMeetingsBehaviour } = this.props.meetingGuidelinesSettings ?? {};

		let maybeSelect = box => {
			let onSelecting = this.props.onSelecting;
			let current = this.state || {};
			let state = selectionState(box);
			const { newStartDate, newEndDate } = calculateSpeedyMeetingDates(
				breaksBetweenMeetingsEnabled,
				breaksBetweenMeetingsBehaviour,
				state.startDate,
				state.endDate,
			);
			state = { ...state, startDate: newStartDate, endDate: newEndDate };

			let { startDate: start, endDate: end } = state;

			if (onSelecting) {
				if (
					(localizer.eq(current.startDate, start, "minutes") && localizer.eq(current.endDate, end, "minutes")) ||
					onSelecting({ start, end, resourceId: this.props.resource }) === false
				)
					return;
			}

			if (
				this.state.start !== state.start ||
				this.state.end !== state.end ||
				this.state.selecting !== state.selecting
			) {
				this.setState(state);
			}
		};

		let selectionState = point => {
			let currentSlot = this.slotMetrics.closestSlotFromPoint(point, getBoundsForNode(node));

			if (!this.state.selecting) {
				this._initialSlot = currentSlot;
			}

			let initialSlot = this._initialSlot;
			if (localizer.lte(initialSlot, currentSlot)) {
				currentSlot = this.slotMetrics.nextSlot(currentSlot);
			} else if (localizer.gt(initialSlot, currentSlot)) {
				initialSlot = this.slotMetrics.nextSlot(initialSlot);
			}

			const selectRange = this.slotMetrics.getRange(
				localizer.min(initialSlot, currentSlot),
				localizer.max(initialSlot, currentSlot),
			);

			return {
				...selectRange,
				selecting: true,

				top: `${selectRange.top}%`,
				height: `${selectRange.height}%`,
			};
		};

		let selectorClicksHandler = (box, actionType) => {
			if (!isEvent(this.containerRef.current, box)) {
				const { startDate } = selectionState(box);
				const { newStartDate, newEndDate } = calculateSpeedyMeetingDates(
					breaksBetweenMeetingsEnabled,
					breaksBetweenMeetingsBehaviour,
					startDate,
				);

				// Remove any event selection
				this.props.onSelectEvent(null);

				this._selectSlot({
					startDate: newStartDate,
					endDate: newEndDate,
					action: actionType,
					box,
				});
			}
			this.setState({ selecting: false });
		};

		selector.on("selecting", maybeSelect);
		selector.on("selectStart", maybeSelect);

		selector.on("beforeSelect", box => {
			if (this.props.selectable !== "ignoreEvents") return;

			return !isEvent(this.containerRef.current, box);
		});

		selector.on("click", box => selectorClicksHandler(box, "click"));

		selector.on("doubleClick", box => selectorClicksHandler(box, "doubleClick"));

		selector.on("select", bounds => {
			if (this.state.selecting) {
				this._selectSlot({ ...this.state, action: "select", bounds });
				this.setState({ selecting: false });
			}
		});

		selector.on("reset", () => {
			if (this.state.selecting) {
				this.setState({ selecting: false });
			}
		});
	};

	_teardownSelectable = () => {
		if (!this._selector) return;
		this._selector.teardown();
		this._selector = null;
	};

	_selectSlot = ({ startDate, endDate, action, bounds, box }) => {
		let current = startDate,
			slots = [];

		while (this.props.localizer.lte(current, endDate)) {
			slots.push(current);
			current = new Date(+current + this.props.step * 60 * 1000); // using Date ensures not to create an endless loop the day DST begins
		}

		notify(this.props.onSelectSlot, {
			slots,
			start: startDate,
			end: endDate,
			resourceId: this.props.resource,
			action,
			bounds,
			box,
			isAllDay: false,
		});
	};

	_select = (...args) => {
		notify(this.props.onSelectEvent, args);
	};

	select = event => e => this._select(event, e);

	_doubleClick = (...args) => {
		notify(this.props.onDoubleClickEvent, args);
	};

	doubleClick = event => e => this._doubleClick(event, e);

	_keyPress = (...args) => {
		notify(this.props.onKeyPressEvent, args);
	};

	keyPress = event => e => this._keyPress(event, e);
}

DayColumn.propTypes = {
	events: PropTypes.array.isRequired,
	backgroundEvents: PropTypes.array.isRequired,
	step: PropTypes.number.isRequired,
	date: PropTypes.instanceOf(Date).isRequired,
	min: PropTypes.instanceOf(Date).isRequired,
	max: PropTypes.instanceOf(Date).isRequired,
	getNow: PropTypes.func.isRequired,
	isNow: PropTypes.bool,

	rtl: PropTypes.bool,

	accessors: PropTypes.object.isRequired,
	components: PropTypes.object.isRequired,
	getters: PropTypes.object.isRequired,
	localizer: PropTypes.object.isRequired,

	showMultiDayTimes: PropTypes.bool,
	culture: PropTypes.string,
	timeslots: PropTypes.number,

	selected: PropTypes.object,
	selectable: PropTypes.oneOf([true, false, "ignoreEvents"]),
	eventOffset: PropTypes.number,
	longPressThreshold: PropTypes.number,

	onSelecting: PropTypes.func,
	onSelectSlot: PropTypes.func.isRequired,
	onSelectEvent: PropTypes.func.isRequired,
	onDoubleClickEvent: PropTypes.func.isRequired,
	onKeyPressEvent: PropTypes.func,

	className: PropTypes.string,
	dragThroughEvents: PropTypes.bool,
	resource: PropTypes.any,

	dayLayoutAlgorithm: DayLayoutAlgorithmPropType,
};

DayColumn.defaultProps = {
	dragThroughEvents: true,
	timeslots: 2,
};

export default DayColumn;
